// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved

import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faUsersCog } from '@fortawesome/free-solid-svg-icons'

import {
  Spinner,
  Warning,
  Table,
  CenteredContainerDisplay,
  TriggerableActionItem as ActionItem,
  Card,
  Grid,
  LabelledDetail as Detail,
  Modal,
  EmployerName,
  Button,
  Feedback,
  SvgIcon
} from '../../'

import { AudienceAdd } from './AudienceAdd'

import {
  decodeBase64,
  hasResponsibility
} from '../../../utils'

import { CHANNEL_SMS_MESSAGEPLUS } from '../../../constants'

import {
  MUTATION_BULKMESSAGE,
  MUTATION_BULKMESSAGE_AUDIENCE
} from '../../../mutations'


const QUERY_BULKMESSAGE_AUDIENCES = gql`
  query($employerId: UUID, $employerGroupId: UUID, $displayEmployerAudiences: Boolean!, $displayEmployerGroupAudiences: Boolean!) {
    employerAudiences: bulkmessageaudiences(employer_Id: $employerId, archived: false) @include (if: $displayEmployerAudiences){
      totalCount
      edges {
        node {
          id
          name
          description
          createdAt
          identities {
            totalCount
          }
        }
      }
    }
    employerGroupAudiences: bulkmessageaudiences(employergroup_Id: $employerGroupId, archived: false) @include (if: $displayEmployerGroupAudiences){
      totalCount
      edges {
        node {
          id
          name
          description
          createdAt
          identities {
            totalCount
          }
        }
      }
    }
  }
`

const QUERY_EMPLOYER_WHATSAPPMENU_PUBLISHED = gql`
  query($employerId: UUID!) {
    whatsappmenus(employer_Id: $employerId) {
      edges {
        node {
          id
          isPublished
        }
      }
    }
  }
`


export class EmployerBulkSMSAudiences extends Component {
  static propTypes = {
    employerId: PropTypes.string,
    employerGroupId: PropTypes.string,
    employerIdentityId: PropTypes.string,
    goToAudienceDetailPage: PropTypes.func.isRequired
  }

  static defaultProps = {
    className: ''
  }

  state = {
    showAudienceAddModal: false,
    showAudienceRemoveModal: false,
    showTelegramInviteModal: false,
    showWhatsappInviteModel: false,
    selectedAudiences: [],
    showTelegramInvitesSentFeedback: false,
    showWhatsappInvitesSentFeedback: false,
    telegramAvailableAsChannel: false,
    actionInProgress: false
  }

  componentDidMount() {
    if (this.props.employerIdentityId) {
      hasResponsibility(this.props.employerIdentityId, 'messageplus_telegram_employer')
        .then(telegramAvailableAsChannel => (
          this.setState({ telegramAvailableAsChannel })
        ))
    }
  }

  getTableRows = (audiences) => (
    audiences.edges.map(({ node: audience }) => (
      <tr id={audience.id} key={`row_${audience.id}`}>
        <td>
          {audience.name}
        </td>
        <td>
          {audience.description}
        </td>
        <td>
          {audience.identities
            ? audience.identities.totalCount
            : '-'
          }
        </td>
        <td>
          {moment(audience.createdAt).format('lll')}
        </td>
      </tr>
    ))
  )

  toggleAudienceAddModal = () => (
    this.setState(prevState => (
      { showAudienceAddModal: !prevState.showAudienceAddModal }
    ))
  )

  toggleAudienceRemoveModal = () => (
    this.setState(prevState => (
      { showAudienceRemoveModal: !prevState.showAudienceRemoveModal }
    ))
  )

  toggleWhatsappInviteModal = () => (
    this.setState(prevState => (
      { showWhatsappInviteModal: !prevState.showWhatsappInviteModal }
    ))
  )

  toggleTelegramInviteModal = () => (
    this.setState(prevState => (
      { showTelegramInviteModal: !prevState.showTelegramInviteModal }
    ))
  )

  selectAudience = (e) => {
    const id = e.target.id.split('-')[1]  // 'checbox-' removed from id (see Checkbox)
    this.setState(prevState => {
      if (prevState.selectedAudiences.includes(id)) {
        const selectedAudiences = [...prevState.selectedAudiences]
        selectedAudiences.splice(selectedAudiences.indexOf(id), 1)  // selected rows minus deselected
        return { selectedAudiences }
      } else {
        return { selectedAudiences: [...prevState.selectedAudiences, id] }
      }
    })
  }

  selectAllAudiences = (audiences) => {
    if (this.state.selectedAudiences.length === audiences.length) {
      this.clearSelectedAudiences()  // deselect all
      return false  // indicating deselect action
    } else {
      this.setState({ selectedAudiences: audiences.map(rr => rr.node.id) })  // select all row ids
      return true  // indicating select action
    }
  }

  clearSelectedAudiences = () => this.setState({ selectedAudiences: [] })

  sendWhatsappInvites = (bulkmessageMutate) => {
    const inviteMessageContent = document.getElementById('waInviteMessageContent').textContent
    const invitees = this.state.selectedAudiences[0]  // TODO: support multiple audiences later

    bulkmessageMutate({
      variables: {
        input: {
          description: `Whatsapp invites (${moment().format('lll')})`,
          content: inviteMessageContent,
          status: 'ready',
          fromIdentity: this.props.employerIdentityId,
          audience: invitees,
          channels: [decodeBase64(CHANNEL_SMS_MESSAGEPLUS)]
        }
      }
    })
    .then(({ data }) => {
      this.setState(
        { showWhatsappInvitesSentFeedback: true },
        this.toggleWhatsappInviteModal
      )
    })
    .catch(() => {
      // TODO: show confirmation / feedback to user... offer retry
      this.toggleWhatsappInviteModal()
    })
  }

  sendTelegramInvites = (bulkmessageMutate) => {
    const inviteMessageContent = document.getElementById('tgInviteMessageContent').textContent
    const invitees = this.state.selectedAudiences[0]  // TODO: support multiple audiences later

    bulkmessageMutate({
      variables: {
        input: {
          description: `Telegram invites (${moment().format('lll')})`,
          content: inviteMessageContent,
          status: 'ready',
          fromIdentity: this.props.employerIdentityId,
          audience: invitees,
          channels: [decodeBase64(CHANNEL_SMS_MESSAGEPLUS)]
        }
      }
    })
    .then(({ data }) => {
      this.setState(
        { showTelegramInvitesSentFeedback: true },
        this.toggleTelegramInviteModal
      )
    })
    .catch(() => {
      // TODO: show confirmation / feedback to user... offer retry
      this.toggleTelegramInviteModal()
    })
  }

  archiveAudience = (bulkmessageaudienceMutate, audienceId) => (
    bulkmessageaudienceMutate({
      variables: {
        input: {
          id: audienceId,
          archived: true
        }
      }
    })
  )

  archiveAudiences = (bulkmessageaudienceMutate, closeModal, refreshData) => {
    this.setState(
      { actionInProgress: true },
      () => {
        const mutationPromises = this.state.selectedAudiences.map(audienceId => (
          this.archiveAudience(bulkmessageaudienceMutate, audienceId)
        ))

        Promise.all(mutationPromises)
          .then(() => {
            closeModal()
            refreshData()
            this.setState({ actionInProgress: false })
          })
      }
    )
  }

  render() {
    return (
      <div className={`slds-size_1-of-1 ${this.props.className}`}>
        <Query
          query={QUERY_BULKMESSAGE_AUDIENCES}
          variables={{
            employerId: this.props.employerId
              ? decodeBase64(this.props.employerId)
              : undefined,
            employerGroupId: this.props.employerGroupId
              ? decodeBase64(this.props.employerGroupId)
              : undefined,
            displayEmployerAudiences: !!this.props.employerId,
            displayEmployerGroupAudiences: !!this.props.employerGroupId
          }}>
          {({ loading, error, data, refetch }) => {
            if (loading) {
              return <Spinner type='tail' />
            }

            if (error) {
              return <Warning>Failed to fetch audience data</Warning>
            }

            const audiences = this.props.employerId
              ? data.employerAudiences
              : data.employerGroupAudiences

            return (
              <Fragment>
                {/* Audience overview section */}
                <div className='slds-grid slds-p-around_large slds-border_bottom'>
                  <Card
                    title='Audiences'
                    icon={
                      <Icon icon={faUsersCog} size='3x' className='slds-m-right_x-small'/>
                    }
                    body={
                      <div>
                        <Grid
                          items={[
                            <Detail
                              label='Number of audiences'
                              detail={audiences.totalCount}
                              detailFontSize="large"
                              detailFontWeight="bold"
                              className='slds-p-bottom_large'
                            />
                          ]}
                        />
                      </div>
                    }
                    paddingTRL={true}
                    className='slds-size_1-of-2'
                  />
                  <CenteredContainerDisplay
                    content={
                      <div className='slds-size_3-of-4 slds-m-left_x-large'>
                        <div className='u-font-size-larger slds-m-bottom_medium'>
                          <b>Actions</b>
                        </div>
                        <ActionItem
                          name='Add new audience'
                          triggerLabel='add'
                          triggerOnClick={this.toggleAudienceAddModal}
                          className='slds-m-bottom_small'
                        />
                        {this.props.employerId &&
                          <Query
                            query={QUERY_EMPLOYER_WHATSAPPMENU_PUBLISHED}
                            variables={{ employerId: decodeBase64(this.props.employerId) }}>
                            {({ loading, error, data }) => {
                              if (loading || error) {
                                return null
                              }

                              if (data.whatsappmenus.edges.length > 0 &&
                                data.whatsappmenus.edges[0].node.isPublished) {
                                return (
                                  <ActionItem
                                    name='Invite audience(s) to use WhatsApp menu'
                                    triggerLabel='invite'
                                    triggerOnClick={this.toggleWhatsappInviteModal}
                                    disabled={this.state.selectedAudiences.length === 0}
                                    className='slds-m-bottom_small'
                                  />
                                )
                              } else {
                                return null
                              }
                            }}
                          </Query>
                        }
                        {this.state.telegramAvailableAsChannel &&
                          <ActionItem
                            name='Invite audience(s) to Telegram'
                            triggerLabel='invite'
                            triggerOnClick={this.toggleTelegramInviteModal}
                            disabled={this.state.selectedAudiences.length === 0}
                            className='slds-m-bottom_small'
                          />
                        }
                        {/*<ActionItem
                          name='Merge selected audiences as new'
                          triggerLabel='merge'
                          triggerOnClick={() => null}
                          disabled={true}
                          className='slds-m-bottom_small'
                        />*/}
                        <Mutation mutation={MUTATION_BULKMESSAGE_AUDIENCE}>
                          {bulkmessageaudienceMutate => (
                            <ActionItem
                              name='Remove selected audience(s)'
                              triggerLabel='remove'
                              triggerOnClick={this.toggleAudienceRemoveModal}
                              disabled={this.state.selectedAudiences.length === 0}
                              className='slds-m-bottom_small'
                            />
                          )}
                        </Mutation>
                      </div>
                    }
                    yAdjustPercentage={-35}
                    useFullWidth={true}
                    className='slds-size_1-of-2'
                  />
                </div>
                <Table
                  columns={["Name", "Description", "Members", "Created at"]}
                  rows={this.getTableRows(audiences)}
                  objectRows={true}
                  selectableRows={audiences.totalCount > 0}
                  selectedRows={this.state.selectedAudiences}
                  onRowSelect={this.selectAudience}
                  onGlobalSelect={this.selectAllAudiences}
                  onClick={audienceId => this.props.goToAudienceDetailPage(audienceId)}
                  // orderBy={this.props.orderBy}
                  // changeSort={this.props.changeSort}
                  // querySortableColumns={{
                  //   'Created at': {
                  //     orderByName: 'created_at'
                  //   }
                  // }}
                />
                {this.state.showAudienceAddModal &&
                  <Modal
                    body={
                      <AudienceAdd
                        employerId={this.props.employerId}
                        employerGroupId={this.props.employerGroupId}
                        onComplete={() => {
                          this.toggleAudienceAddModal()
                          refetch()
                        }}
                        className='slds-m-around_large'
                      />
                    }
                    contentPadding='none'
                    onCloseClick={this.toggleAudienceAddModal}
                    sizeLarge={true}
                  />
                }
                {this.state.showAudienceRemoveModal &&
                  <Modal
                    body={
                      <div className='slds-m-around_large slds-text-align_center'>
                        <div className='u-font-size-larger'>
                          Are you sure you want to remove the selected audience?
                        </div>
                        <p className='slds-p-top_medium'>
                          <SvgIcon
                            variant='utility'
                            symbol='info'
                            colour='light'
                            size='small'
                            className='slds-p-right_x-small'
                          />
                          Bulk messages created using this audience will still be accessable.
                        </p>
                      </div>
                    }
                    footer={
                      <div className='slds-align_absolute-center'>
                        <Button
                          onClick={this.toggleAudienceRemoveModal}
                          className='slds-p-right_small'>
                          Cancel
                        </Button>
                        <Mutation mutation={MUTATION_BULKMESSAGE_AUDIENCE}>
                          {bulkmessageaudienceMutate => (
                            <Button
                              type='destructive'
                              onClick={() => (
                                this.archiveAudiences(
                                  bulkmessageaudienceMutate,
                                  this.toggleAudienceRemoveModal,
                                  refetch
                                )
                              )}>
                              Proceed
                            </Button>
                          )}
                        </Mutation>
                      </div>
                    }
                    contentPadding='none'
                    onCloseClick={this.toggleAudienceRemoveModal}
                  />
                }
                {this.state.showWhatsappInviteModal &&
                  <Modal
                    body={
                      <div className='slds-m-around_large u-font-size-larger'>
                        The following SMS message will be sent to audience members who have not yet used Messageplus' WhatsApp service:
                        <div
                          id='waInviteMessageContent'
                          className='slds-m-top_medium slds-m-bottom_large slds-p-around_small'
                          style={{
                            wordWrap: 'break-word',
                            workBreak: 'break-word',
                            whiteSpace: 'pre-line',
                            letterSpacing: '0.75px',
                            fontSize: '0.95em',
                            border: '1px solid grey',
                            borderRadius: '5px',
                            backgroundColor: '#DDECCF'
                          }}>
                          <EmployerName id={this.props.employerId} /> has invited you to engage with their MessagePlus WhatsApp menu.
                          Please click on https://wa.me/27600923284 and type 'hi' to start.
                        </div>
                        <div className='slds-text-body_small'>
                          <b>Note</b> that you will be charged for each SMS invite message; an SMS message will be sent to each member in the selected audience.
                        </div>
                      </div>
                    }
                    footer={
                      <div className='slds-align_absolute-center'>
                        <Button
                          onClick={this.toggleWhatsappInviteModal}
                          className='slds-p-right_small'>
                          Cancel
                        </Button>
                        <Mutation mutation={MUTATION_BULKMESSAGE}>
                          {bulkmessageMutate => (
                            <Button
                              type='brand'
                              onClick={() => this.sendWhatsappInvites(bulkmessageMutate)}>
                              Proceed
                            </Button>
                          )}
                        </Mutation>
                      </div>
                    }
                    contentPadding='none'
                  />
                }
                {this.state.showTelegramInviteModal &&
                  <Modal
                    body={
                      <div className='slds-m-around_large u-font-size-larger'>
                        The following SMS message will be sent to audience members who have not yet engaged with MessagePlus' Telegram service:
                        <div
                          id='tgInviteMessageContent'
                          className='slds-m-top_medium slds-m-bottom_large slds-p-around_small'
                          style={{
                            wordWrap: 'break-word',
                            workBreak: 'break-word',
                            whiteSpace: 'pre-line',
                            letterSpacing: '0.75px',
                            fontSize: '0.95em',
                            border: '1px solid grey',
                            borderRadius: '5px',
                            backgroundColor: '#DDECCF'
                          }}>
                          <EmployerName id={this.props.employerId} /> has invited you to Telegram to
                          receive important notifications. Please click on https://t.me/MessagePlus_sender_bot to register.
                        </div>
                        <div className='slds-text-body_small'>
                          <b>Note</b> that you will be charged for each SMS invite message; an SMS message will be sent to each member in the selected audience.
                        </div>
                      </div>
                    }
                    footer={
                      <div className='slds-align_absolute-center'>
                        <Button
                          onClick={this.toggleTelegramInviteModal}
                          className='slds-p-right_small'>
                          Cancel
                        </Button>
                        <Mutation mutation={MUTATION_BULKMESSAGE}>
                          {bulkmessageMutate => (
                            <Button
                              type='brand'
                              disabled={this.state.actionInProgress}
                              onClick={() => this.sendTelegramInvites(bulkmessageMutate)}>
                              Proceed
                            </Button>
                          )}
                        </Mutation>
                      </div>
                    }
                    contentPadding='none'
                  />
                }
              </Fragment>
            )
          }}
        </Query>
        {this.state.showTelegramInvitesSentFeedback &&
          <Feedback
            message='Invites sent'
            fadeDuration={2500}  // 2.5s matches .feedback-fade css class animation
            unmount={() => (
              this.setState({ showTelegramInvitesSentFeedback: false })
            )}
            icon={
              <SvgIcon
                variant='utility'
                symbol='check'
                colour='inverse'
                className='slds-align_absolute-center'
              />
            }
          />
        }
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/messageplus/employer-bulk-sms-audiences/index.js